import { useTranslation } from "react-i18next";
import {
  Box,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Table,
  TableContainer,
  Typography,
  CircularProgress,
} from "@mui/material";

import { EntityBranchDetails } from "../../../Entity/api/types/entityBranches.types";
import { formatLocalizedTime } from "../../../../utils/formatDate";

const WorkingHoursTab = ({
  isBranchDataLoading,
  branchData,
}: {
  isBranchDataLoading: boolean;
  branchData?: EntityBranchDetails;
}) => {
  const { t, i18n } = useTranslation("profiles", { keyPrefix: "entityProfileBranch" });
  const { t: tDays } = useTranslation("common", { keyPrefix: "day" });

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("workingDaysHours")}</Typography>
      {isBranchDataLoading && <CircularProgress sx={styles.loading} />}
      {!isBranchDataLoading && (
        <TableContainer sx={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell component="th" sx={styles.tableHeaderCell}>
                  {t("day")}
                </TableCell>
                <TableCell component="th" sx={styles.tableHeaderCell}>
                  {t("openingHours")}
                </TableCell>
                <TableCell component="th" sx={styles.tableHeaderCell}>
                  {t("status")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {branchData?.workingHours.map(({ dayOfWeek, closingTime, openingTime, isClosed }, index) => {
                return (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:nth-of-type(odd)": { backgroundColor: "var(--Gray-50, #F9FAFB)" },
                    }}
                  >
                    <TableCell component="td" scope="row" sx={styles.tableDayCell}>
                      {tDays(dayOfWeek.toLowerCase())}
                    </TableCell>
                    <TableCell component="td" sx={styles.tableTimeCell}>
                      {formatLocalizedTime(openingTime, i18n.language)} -{" "}
                      {formatLocalizedTime(closingTime, i18n.language)}
                    </TableCell>
                    <TableCell component="td">
                      <Typography sx={isClosed ? styles.tableStatusCellClosed : styles.tableStatusCellOpen}>
                        {isClosed ? t("closed") : t("open")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default WorkingHoursTab;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "0 16px",
  },
  title: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  loading: {
    margin: "50px auto",
  },
  tableContainer: {
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
  },
  tableHeaderCell: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  tableDayCell: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  tableTimeCell: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  tableStatusCellOpen: {
    fontFamily: "Montserrat",
    color: "var(--Success-700, #067647)",
    textAlign: "center",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    borderRadius: "16px",
    border: "1px solid var(--Success-200, #ABEFC6)",
    background: "var(--Success-50, #ECFDF3)",
    padding: "2px 8px",
    width: "fit-content",
  },
  tableStatusCellClosed: {
    fontFamily: "Montserrat",
    color: "var(--Error-700, #B42318)",
    textAlign: "center",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    borderRadius: "16px",
    border: "1px solid var(--Error-200, #FECDCA)",
    background: "var(--Error-50, #FEF3F2)",
    padding: "2px 8px",
    width: "fit-content",
  },
};
